import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
    Lightning,
    SignOut,
    Gauge,
    UserCircle,
    ShieldCheck,
    List,
    X,
} from "@phosphor-icons/react";

/**
 * Top-level navigation. Two layouts:
 *   - Desktop (md+): inline links + auth controls
 *   - Mobile (<md): hamburger button → fullscreen overlay menu
 *
 * Landing-only anchor links (`#six-steps`, `#why`, `#case-studies`, `#pricing`)
 * scroll to the corresponding sections on the home page. Clicking from any other
 * page routes back to "/" with the hash so the browser can scroll once mounted.
 */
const LANDING_LINKS = [
    { href: "#six-steps", label: "HOW IT WORKS" },     // → AssemblyLineWalkthrough
    { href: "#why", label: "FEATURES" },                // → "Tools forged for the finish line"
    { href: "#case-studies", label: "RESULTS" },        // → "Real Operators. Real Results."
    { href: "#pricing", label: "PRICING" },
];

export default function NavBar({ variant = "landing" }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Lock body scroll while the mobile menu is open
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const goAnchor = (href) => {
        setOpen(false);
        // If we're on landing, just let the browser scroll. Otherwise route home.
        if (location.pathname === "/") {
            const id = href.replace("#", "");
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/${href}`);
        }
    };

    return (
        <>
            <nav
                data-testid="main-navbar"
                className="sticky top-0 z-50 glass-panel px-6 md:px-12 py-4 flex justify-between items-center border-b border-white/10"
            >
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2 group"
                    onClick={() => setOpen(false)}
                >
                    <div className="w-8 h-8 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={18} className="text-white" />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-heading font-black tracking-tighter text-lg">
                            NEXUS<span className="text-electric-blue">.</span>LAB
                        </span>
                        <span className="label-mono text-[10px] mt-0.5">CREATOR SYNDICATE</span>
                    </div>
                </Link>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-5">
                    {variant === "landing" && LANDING_LINKS.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => goAnchor(l.href)}
                            data-testid={`nav-${l.href.replace("#", "")}-link`}
                            className="label-mono text-xs hover:text-electric-blue transition-colors"
                        >
                            {l.label}
                        </button>
                    ))}
                    {user ? (
                        <DesktopAuthArea
                            user={user}
                            onLogout={() => { logout(); navigate("/"); }}
                            onDashboard={() => navigate("/dashboard")}
                            onAdmin={() => navigate("/admin")}
                        />
                    ) : (
                        <DesktopGuestArea />
                    )}
                </div>

                {/* Mobile: hamburger trigger */}
                <button
                    data-testid="nav-hamburger-btn"
                    onClick={() => setOpen(true)}
                    aria-label="Open menu"
                    className="md:hidden p-2 border border-white/15 hover:border-electric-blue text-white"
                >
                    <List size={20} weight="bold" />
                </button>
            </nav>

            {/* Mobile fullscreen menu */}
            {open && (
                <MobileMenu
                    variant={variant}
                    user={user}
                    onClose={() => setOpen(false)}
                    onAnchor={goAnchor}
                    onLogout={() => { logout(); navigate("/"); setOpen(false); }}
                    onDashboard={() => { navigate("/dashboard"); setOpen(false); }}
                    onAdmin={() => { navigate("/admin"); setOpen(false); }}
                />
            )}
        </>
    );
}

function DesktopAuthArea({ user, onLogout, onDashboard, onAdmin }) {
    return (
        <div className="flex items-center gap-3">
            {user.subscription_active && (
                <button
                    data-testid="nav-dashboard-btn"
                    onClick={onDashboard}
                    className="flex items-center gap-1.5 label-mono text-xs hover:text-electric-blue transition-colors"
                >
                    <Gauge size={14} weight="bold" />
                    DASHBOARD
                </button>
            )}
            {user.is_admin && (
                <button
                    data-testid="nav-admin-btn"
                    onClick={onAdmin}
                    className="flex items-center gap-1.5 label-mono text-xs text-electric-blue hover:text-white transition-colors"
                >
                    <ShieldCheck size={14} weight="fill" />
                    ADMIN
                </button>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-white/10">
                <UserCircle size={16} className="text-electric-blue" />
                <span data-testid="nav-user-display" className="text-xs font-medium">
                    {user.display_name}
                </span>
            </div>
            <button
                data-testid="nav-logout-btn"
                onClick={onLogout}
                className="text-muted-foreground hover:text-white transition-colors"
                aria-label="Logout"
            >
                <SignOut size={18} />
            </button>
        </div>
    );
}

function DesktopGuestArea() {
    return (
        <div className="flex items-center gap-3">
            <Link
                to="/login"
                data-testid="nav-login-link"
                className="label-mono text-xs hover:text-electric-blue transition-colors"
            >
                LOGIN
            </Link>
            <Link
                to="/signup"
                data-testid="nav-signup-link"
                className="bg-electric-blue hover:bg-electric-blue-dark text-white px-4 py-2 text-xs font-bold tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(0,123,255,0.5)]"
            >
                Claim Seat
            </Link>
        </div>
    );
}

function MobileMenu({ variant, user, onClose, onAnchor, onLogout, onDashboard, onAdmin }) {
    return (
        <div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col"
        >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <span className="label-mono text-electric-blue text-xs">MENU</span>
                <button
                    data-testid="nav-mobile-close-btn"
                    onClick={onClose}
                    aria-label="Close menu"
                    className="p-2 border border-white/15 hover:border-electric-blue text-white"
                >
                    <X size={20} weight="bold" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1">
                {variant === "landing" && LANDING_LINKS.map((l) => (
                    <button
                        key={l.href}
                        onClick={() => onAnchor(l.href)}
                        data-testid={`nav-mobile-${l.href.replace("#", "")}-link`}
                        className="text-left font-heading font-black text-2xl py-4 border-b border-white/5 hover:text-electric-blue transition-colors"
                    >
                        {l.label}
                    </button>
                ))}
                {user ? (
                    <div className="mt-6 flex flex-col gap-1">
                        {user.subscription_active && (
                            <button
                                onClick={onDashboard}
                                data-testid="nav-mobile-dashboard-btn"
                                className="text-left font-heading font-black text-2xl py-4 border-b border-white/5 hover:text-electric-blue transition-colors flex items-center gap-3"
                            >
                                <Gauge size={20} weight="bold" />
                                DASHBOARD
                            </button>
                        )}
                        {user.is_admin && (
                            <button
                                onClick={onAdmin}
                                data-testid="nav-mobile-admin-btn"
                                className="text-left font-heading font-black text-2xl py-4 border-b border-white/5 text-electric-blue flex items-center gap-3"
                            >
                                <ShieldCheck size={20} weight="fill" />
                                ADMIN
                            </button>
                        )}
                        <button
                            onClick={onLogout}
                            data-testid="nav-mobile-logout-btn"
                            className="text-left font-heading font-black text-2xl py-4 border-b border-white/5 text-muted-foreground hover:text-white transition-colors flex items-center gap-3"
                        >
                            <SignOut size={20} weight="bold" />
                            LOGOUT
                        </button>
                        <div className="mt-6 px-4 py-3 bg-[#0A0A0A] border border-white/10 flex items-center gap-2">
                            <UserCircle size={20} className="text-electric-blue" />
                            <span className="text-sm font-medium">{user.display_name}</span>
                        </div>
                    </div>
                ) : (
                    <div className="mt-8 flex flex-col gap-3">
                        <Link
                            to="/login"
                            onClick={onClose}
                            data-testid="nav-mobile-login-link"
                            className="text-center label-mono text-sm border border-white/15 hover:border-electric-blue py-4 transition-colors"
                        >
                            LOGIN
                        </Link>
                        <Link
                            to="/signup"
                            onClick={onClose}
                            data-testid="nav-mobile-signup-link"
                            className="text-center bg-electric-blue hover:bg-electric-blue-dark text-white py-4 text-sm font-bold tracking-wider uppercase transition-all"
                        >
                            CLAIM YOUR SEAT
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
